import * as React from "react";
import {
  FluentProvider,
  Spinner,
  Body1,
  Caption1,
  Button,
  Toaster,
  useToastController,
  useId,
  Toast,
  ToastTitle,
  ToastBody,
  ToastTrigger,
  Link,
  tokens,
  makeStyles,
} from "@fluentui/react-components";

import { appTheme } from "./styles/theme";
import { useGlpiSession } from "./hooks/useGlpiSession";
import { SettingsScreen } from "./components/SettingsScreen";
import { ActionPicker } from "./components/ActionPicker";
import { CreateTicketForm } from "./components/CreateTicketForm";
import { AddFollowupForm } from "./components/AddFollowupForm";
import { AddTicketTaskForm } from "./components/AddTicketTaskForm";
import { ErrorBar } from "./components/ErrorBar";

type View = "picker" | "create" | "followup" | "task" | "settings";

const useStyles = makeStyles({
  shell: {
    minHeight: "100vh",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  centerScreen: {
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    textAlign: "center",
  },
});

export default function App(): React.ReactElement {
  const styles = useStyles();
  const session = useGlpiSession();
  const [view, setView] = React.useState<View>("picker");
  const toasterId = useId("glpi-toaster");
  const { dispatchToast } = useToastController(toasterId);

  function notifyTicketCreated(info: {
    id: number;
    url: string;
    taskFailed?: boolean;
    taskRetry?: () => Promise<void>;
    attachmentsAttempted?: number;
    attachmentsFailed?: string[];
  }): void {
    const hasAttachmentFailure = (info.attachmentsFailed?.length ?? 0) > 0;
    const intent = info.taskFailed || hasAttachmentFailure ? "warning" : "success";

    dispatchToast(
      <Toast>
        <ToastTitle
          action={
            <ToastTrigger>
              <Link>Fermer</Link>
            </ToastTrigger>
          }
        >
          Ticket #{info.id} créé
        </ToastTitle>
        <ToastBody>
          <Link href={info.url} target="_blank" rel="noopener noreferrer">
            Ouvrir dans GLPI
          </Link>

          {typeof info.attachmentsAttempted === "number" && info.attachmentsAttempted > 0 && (
            <div style={{ marginTop: 6 }}>
              <Caption1>
                {hasAttachmentFailure
                  ? `Pièces jointes : ${
                      info.attachmentsAttempted - (info.attachmentsFailed?.length ?? 0)
                    }/${info.attachmentsAttempted} OK (échec: ${info.attachmentsFailed?.join(", ")})`
                  : `Pièces jointes : ${info.attachmentsAttempted}/${info.attachmentsAttempted} OK`}
              </Caption1>
            </div>
          )}

          {info.taskFailed && (
            <div style={{ marginTop: 6 }}>
              <Caption1>
                Tâche générique non créée.{" "}
                <Link
                  appearance="default"
                  onClick={async () => {
                    if (info.taskRetry) {
                      try {
                        await info.taskRetry();
                        dispatchToast(
                          <Toast>
                            <ToastTitle>Tâche créée</ToastTitle>
                          </Toast>,
                          { intent: "success" }
                        );
                      } catch (e) {
                        dispatchToast(
                          <Toast>
                            <ToastTitle>Réessai échoué</ToastTitle>
                            <ToastBody>{(e as Error).message}</ToastBody>
                          </Toast>,
                          { intent: "error" }
                        );
                      }
                    }
                  }}
                >
                  Réessayer
                </Link>
              </Caption1>
            </div>
          )}
        </ToastBody>
      </Toast>,
      { intent, timeout: intent === "warning" ? 8000 : 4500 }
    );
    setView("picker");
  }

  function notifyTasksAdded(info: {
    ticketId: number;
    taskCount: number;
    url: string;
    attachmentsAttempted?: number;
    attachmentsFailed?: string[];
  }): void {
    const hasAttachmentFailure = (info.attachmentsFailed?.length ?? 0) > 0;
    const intent = hasAttachmentFailure ? "warning" : "success";

    dispatchToast(
      <Toast>
        <ToastTitle>
          {info.taskCount > 1
            ? `${info.taskCount} tâches ajoutées au ticket #${info.ticketId}`
            : `Tâche ajoutée au ticket #${info.ticketId}`}
        </ToastTitle>
        <ToastBody>
          <Link href={info.url} target="_blank" rel="noopener noreferrer">
            Ouvrir dans GLPI
          </Link>
          {typeof info.attachmentsAttempted === "number" && info.attachmentsAttempted > 0 && (
            <div style={{ marginTop: 6 }}>
              <Caption1>
                {hasAttachmentFailure
                  ? `Pièces jointes : ${
                      info.attachmentsAttempted - (info.attachmentsFailed?.length ?? 0)
                    }/${info.attachmentsAttempted} OK (échec: ${info.attachmentsFailed?.join(", ")})`
                  : `Pièces jointes : ${info.attachmentsAttempted}/${info.attachmentsAttempted} OK`}
              </Caption1>
            </div>
          )}
        </ToastBody>
      </Toast>,
      { intent, timeout: intent === "warning" ? 8000 : 4500 }
    );
    setView("picker");
  }

  function notifyFollowupAdded(info: { ticketId: number; followupId: number; url: string }): void {
    dispatchToast(
      <Toast>
        <ToastTitle>Suivi ajouté au ticket #{info.ticketId}</ToastTitle>
        <ToastBody>
          <Link href={info.url} target="_blank" rel="noopener noreferrer">
            Ouvrir dans GLPI
          </Link>
        </ToastBody>
      </Toast>,
      { intent: "success", timeout: 4500 }
    );
    setView("picker");
  }

  function notifyError(msg: string): void {
    dispatchToast(
      <Toast>
        <ToastTitle>Erreur</ToastTitle>
        <ToastBody>{msg}</ToastBody>
      </Toast>,
      { intent: "error", timeout: 7000 }
    );
  }

  let body: React.ReactElement;

  if (session.status === "missing-token" || view === "settings") {
    body = (
      <SettingsScreen
        onSaved={() => {
          // Après sauvegarde du token, on relance la connexion.
          setView("picker");
          void session.reconnect();
        }}
      />
    );
  } else if (session.status === "idle" || session.status === "connecting") {
    body = (
      <div className={styles.centerScreen}>
        <Spinner />
        <Body1>Connexion à GLPI…</Body1>
      </div>
    );
  } else if (session.status === "error") {
    body = (
      <div className={styles.centerScreen}>
        <ErrorBar message={session.error} title="Connexion GLPI" />
        <Button appearance="primary" onClick={() => void session.retry()}>
          Réessayer
        </Button>
        <Button appearance="subtle" onClick={() => void session.reset()}>
          Reconfigurer le user-token
        </Button>
      </div>
    );
  } else if (view === "create") {
    body = (
      <CreateTicketForm
        onBack={() => setView("picker")}
        onTicketCreated={notifyTicketCreated}
        onError={notifyError}
      />
    );
  } else if (view === "followup") {
    body = (
      <AddFollowupForm
        onBack={() => setView("picker")}
        onFollowupAdded={notifyFollowupAdded}
        onError={notifyError}
      />
    );
  } else if (view === "task") {
    body = (
      <AddTicketTaskForm
        onBack={() => setView("picker")}
        onTasksAdded={notifyTasksAdded}
        onError={notifyError}
      />
    );
  } else {
    body = (
      <ActionPicker
        onPickCreate={() => setView("create")}
        onPickAddFollowup={() => setView("followup")}
        onPickAddTask={() => setView("task")}
        onOpenSettings={() => setView("settings")}
      />
    );
  }

  return (
    <FluentProvider theme={appTheme}>
      <div className={styles.shell}>{body}</div>
      <Toaster toasterId={toasterId} position="bottom" />
    </FluentProvider>
  );
}
